import { useEffect, useMemo, useState } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { Box, CircularProgress } from '@mui/material'
import { usePortalStore } from '../stores/portalStore'
import { usePreferencesStore } from '../stores/preferencesStore'

interface PortalThemeProviderProps {
  children: React.ReactNode
}

export default function PortalThemeProvider({ children }: PortalThemeProviderProps) {
  const { config, loading, fetchConfig } = usePortalStore()
  const { themeMode } = usePreferencesStore()
  const [mode, setMode] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  useEffect(() => {
    if (themeMode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      setMode(mq.matches ? 'dark' : 'light')
      const handler = (e: MediaQueryListEvent) => setMode(e.matches ? 'dark' : 'light')
      mq.addEventListener('change', handler)
      return () => mq.removeEventListener('change', handler)
    }
    setMode(themeMode === 'dark' ? 'dark' : 'light')
  }, [themeMode])

  const theme = useMemo(() => {
    const primary = config?.primaryColor || '#003399'
    const secondary = config?.secondaryColor || '#0055aa'

    return createTheme({
      palette: {
        mode,
        primary: { main: primary },
        secondary: { main: secondary },
        background: {
          default: mode === 'light' ? '#f8fafc' : '#0f172a',
          paper: mode === 'light' ? '#ffffff' : '#1e293b',
        },
        text: {
          primary: mode === 'light' ? '#0f172a' : '#f8fafc',
          secondary: mode === 'light' ? '#475569' : '#94a3b8',
        },
        divider: mode === 'light' ? '#e2e8f0' : '#334155',
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      },
      shape: { borderRadius: 8 },
    })
  }, [mode, config?.primaryColor, config?.secondaryColor])

  if (loading && !config) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f8fafc',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}
